// Utilitários do modelo "Internacional" (topo 16 · busca 19 · card 13 · rodapé 13) — a linguagem de
// layout de um site de referência internacional (Montserrat única, TUDO em pílula, topo que vira
// cápsula flutuante ao rolar, herói de mídia com degradê para o preto e "lábio" de cantos enormes,
// cards sem caixa com aba preta, faixa de contato com letra-marca gigante) reproduzida em CSS
// modules NOSSOS, com as CORES DO TENANT. Só as 4 peças do Internacional importam daqui — não é
// componente compartilhado dos ~300 sites. Reusa (importa, não copia) os helpers genéricos do
// Horizonte/Atelier/Larissa: contatos, menu, redes, WhatsApp, URL/foto do imóvel, preços rotulados,
// logo sem placa e a régua de contraste (ajustarAte/serveDeBase).
import type { CSSProperties } from 'react';
import { getContrastRatio, removerAcentos } from '../../../utils/FunctionsGlobals';
import type { ItemMenu } from '../horizonte/horizonte';
import { hexParaRgba, misturarCores } from '../horizonte/horizonte';
import { ajustarAte, hexValido, luminancia, serveDeBase } from '../larissa/larissa';

export { contatosDoTenant, hexParaRgba, linkWhatsApp, linkWhatsAppDoTenant, menuDoTenant, mesmaRota, misturarCores, redesDoTenant, useMovimentoReduzido } from '../horizonte/horizonte';
export type { ItemMenu, TelefoneTenant } from '../horizonte/horizonte';
export { fotoOtimizada, fotosDoImovel, nomeDaEmpresa, stripHtml, urlDoImovel } from '../atelier/atelier';
export { precosRotulados, semCaixaAlta, useLogoSemPlaca } from '../larissa/larissa';
export type { PrecoRotulado } from '../larissa/larissa';

// Curva das entradas do modelo (desacelera longo no fim) — a mesma dos modelos 8–11.
export const EASE_INTERNACIONAL = [0.22, 1, 0.36, 1] as const;

/**
 * Escala de RESERVA = tokens MEDIDOS no site de referência (getComputedStyle / pixels dos prints de
 * 21/10): marinho dos títulos e botões, marinho mais fundo da faixa de contato, cinza-claro das seções,
 * preto do pé do herói, quase-preto do texto dos cards. Entra só onde o tenant não tem cor utilizável.
 */
export const RESERVA_IN = {
    base: '#001a72',
    hover: '#131176',
    tinta: '#001a72',
    fundo: '#f7f7f7',
    cinza: '#5f6168',
    borda: '#e5e5e5',
    preto: '#000000',
    texto: '#0a0a0a',
};

export interface PaletaInternacional {
    /** cor escura do modelo: cápsula do topo, botões, faixa de contato (texto branco lê ≥ 5.5:1). */
    base: string;
    /** base mais funda (hover dos botões sólidos). */
    hover: string;
    /** títulos e links sobre fundo claro (a base escurecida até ler ≥ 7:1 sobre o branco). */
    tinta: string;
    /** cinza-claro das seções e do "lábio" do herói, com pitada da base. */
    fundo: string;
    /** texto secundário sobre `fundo`/branco (≥ 4.5:1). */
    cinza: string;
    /** bordas e divisórias. */
    borda: string;
    /** texto secundário SOBRE a base escura (≥ 4.5:1). */
    sobreBase: string;
    /** letra-marca gigante da faixa de contato: tom mais claro da base (decorativa, baixo contraste de propósito). */
    marca: string;
    /** abas não selecionadas da pílula segmentada (base clareada, ≥ 4.5:1 sobre a pílula translúcida). */
    abaSuave: string;
    /** preto do pé do herói e da aba dos cards. */
    preto: string;
    /** texto dos cards (quase-preto). */
    texto: string;
}

/** Pílula segmentada do herói: fundo branco translúcido — sobre o véu escuro resulta ≈ #e7e7e7 (medido). */
const PILULA_SEGMENTADA = '#e7e7e7';

/**
 * Mapeamento p/ os 300 sites (mesma regra do paletaLarissa): base = a cor MAIS ESCURA do tenant que serve
 * (saturada ou preto/grafite; cor1 em empate, nunca branco/cinza claro), escurecida até texto branco ler
 * ≥ 5.5:1. tinta = base escurecida até ≥ 7:1 sobre o branco. fundo = cinza-claro medido com pitada da base.
 * cinza ≥ 4.5:1 sobre o fundo. Sem cor no tenant → o marinho da referência.
 */
export const paletaInternacional = (site: any): PaletaInternacional => {
    const cor1 = hexValido(site?.cor1);
    const cor2 = hexValido(site?.cor2);
    const bases = [cor1, cor2].filter(serveDeBase);
    // a cor2 só toma o lugar da principal quando é CLARAMENTE mais escura
    const bruta = bases.length === 2 && luminancia(cor2) < luminancia(cor1) * 0.6 ? cor2 : bases[0] ?? '';

    const base = bruta ? ajustarAte(bruta, '#05081a', '#ffffff', 5.5) : RESERVA_IN.base;
    const hover = bruta ? misturarCores(base, '#000000', 0.28) : RESERVA_IN.hover;
    const tinta = bruta ? ajustarAte(base, '#05081a', '#ffffff', 7) : RESERVA_IN.tinta;
    const fundo = bruta ? misturarCores(RESERVA_IN.fundo, base, 0.02) : RESERVA_IN.fundo;
    const cinzaBruto = bruta ? misturarCores(RESERVA_IN.cinza, base, 0.1) : RESERVA_IN.cinza;
    const cinza = (() => {
        for (let t = 0; t <= 1.0001; t += 0.05) {
            const c = t === 0 ? cinzaBruto : misturarCores(cinzaBruto, RESERVA_IN.texto, t);
            if (getContrastRatio(c, fundo) >= 4.5) return c;
        }
        return tinta;
    })();
    const sobreBase = (() => {
        const partida = misturarCores(base, '#ffffff', 0.6);
        for (let t = 0; t <= 1.0001; t += 0.05) {
            const c = t === 0 ? partida : misturarCores(partida, '#ffffff', t);
            if (getContrastRatio(c, base) >= 4.5) return c;
        }
        return '#ffffff';
    })();
    // abas não selecionadas: parte da base clareada (como a referência) e escurece até ler sobre a pílula
    const abaSuave = (() => {
        const partida = misturarCores(base, '#ffffff', 0.32);
        for (let t = 0; t <= 1.0001; t += 0.05) {
            const c = t === 0 ? partida : misturarCores(partida, base, t);
            if (getContrastRatio(c, PILULA_SEGMENTADA) >= 4.5) return c;
        }
        return tinta;
    })();

    return {
        base,
        hover,
        tinta,
        fundo,
        cinza,
        borda: bruta ? misturarCores(RESERVA_IN.borda, base, 0.04) : RESERVA_IN.borda,
        sobreBase,
        marca: misturarCores(base, '#ffffff', 0.3),
        abaSuave,
        preto: RESERVA_IN.preto,
        texto: RESERVA_IN.texto,
    };
};

/** Variáveis CSS (--in-*) que os 4 módulos consomem; o Builder pode sobrescrevê-las por CSS custom. */
export const varsInternacional = (site: any): CSSProperties => {
    const p = paletaInternacional(site);
    return {
        '--in-base': p.base,
        '--in-hover': p.hover,
        '--in-tinta': p.tinta,
        '--in-fundo': p.fundo,
        '--in-cinza': p.cinza,
        '--in-borda': p.borda,
        '--in-sobre-base': p.sobreBase,
        '--in-marca': p.marca,
        '--in-aba-suave': p.abaSuave,
        '--in-preto': p.preto,
        '--in-texto': p.texto,
        // cápsula flutuante do topo: base a 88% + blur (medido na referência)
        '--in-capsula': hexParaRgba(p.base, 0.88, 'rgba(0, 26, 114, ALPHA)'),
        '--in-sombra': hexParaRgba(p.base, 0.16, 'rgba(0, 26, 114, ALPHA)'),
    } as CSSProperties;
};

/** Opacidade do VIDRO da cápsula do topo rolando (pedido de 21/10: "seguir o rolamento porém mais transparente"). */
const OPACIDADE_VIDRO = 0.66;
/** Escurece `cor` rumo ao quase-preto do modelo até `serve(c)`; a própria cor quando já serve. */
const escurecerAte = (cor: string, serve: (_cor: string) => boolean): string => {
    for (let t = 0; t <= 1.0001; t += 0.05) {
        const c = t === 0 ? cor : misturarCores(cor, '#05081a', t);
        if (serve(c)) return c;
    }
    return '#05081a';
};

/**
 * Cápsula do topo ROLANDO: vidro a 66% + blur — na home a base do tenant, nas internas o branco. Para o texto ler AA
 * (≥ 4,5:1) com o PIOR fundo atrás do vidro (branco atrás da base; preto atrás do branco), quem escurece é a COR — a
 * base do vidro (texto branco) ou a tinta do texto (vidro branco) —, nunca a transparência. Só o TopoInternacional usa.
 */
export const varsCapsulaRolando = (site: any): CSSProperties => {
    const p = paletaInternacional(site);
    const vidroBase = escurecerAte(p.base, (c) => getContrastRatio('#ffffff', misturarCores('#ffffff', c, OPACIDADE_VIDRO)) >= 4.5);
    const brancoSobrePreto = misturarCores('#000000', '#ffffff', OPACIDADE_VIDRO);
    const tintaVidro = escurecerAte(p.tinta, (c) => getContrastRatio(c, brancoSobrePreto) >= 4.5);
    return {
        '--in-capsula-rolando': hexParaRgba(vidroBase, OPACIDADE_VIDRO, 'rgba(0, 26, 114, ALPHA)'),
        '--in-vidro': `rgba(255, 255, 255, ${OPACIDADE_VIDRO})`,
        '--in-tinta-vidro': tintaVidro,
    } as CSSProperties;
};

/** Gate do modelo #12 "Internacional" = topo 16 (mesmo critério de ehModeloLarissa/ehModeloPanorama). */
export const ehModeloInternacional = (site: any): boolean => Number(site?.modelo_topo) === 16;

/**
 * Páginas internas no visual do modelo (/imoveis, ficha, /condominios, /empreendimento): o mesmo esquema do
 * varsLarissaInterna, só o necessário — as vars --in-* e Montserrat na página toda (o globals.css aplica a fonte
 * direto em div/p/a/span/button; redefinir --fonte-principal na raiz é o mecanismo das peças). Fora do topo 16:
 * null ⇒ nada muda.
 */
export const varsInternacionalInterna = (site: any): CSSProperties | null => {
    if (!ehModeloInternacional(site)) return null;
    return {
        ...varsInternacional(site),
        '--fonte-principal': "'Montserrat', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    } as CSSProperties;
};

// ---------------------------------------------------------------------------
// Imóvel: finalidade em caixa alta, endereço/local e atributos "3 QUARTOS • 2 SUÍTES • 2 VAGAS • 120 M²".
// ---------------------------------------------------------------------------

/** Etiqueta branca do card ("À VENDA" / "PARA ALUGAR" / "TEMPORADA") — o CSS põe em caixa alta. */
export const finalidadeDoImovel = (slide: any): string => (slide?.venda ? 'À venda' : slide?.aluguel ? 'Para alugar' : slide?.temporada ? 'Temporada' : '');

/**
 * Linha de local do card (a referência mostra o endereço): rua só quando o imóvel permite
 * (`mostrar_endereco`), senão bairro e cidade. Sem nada, o tipo.
 */
export const enderecoDoImovel = (slide: any): string => {
    const rua = slide?.mostrar_endereco && slide?.endereco ? String(slide.endereco).trim() : '';
    const partes = [rua, slide?.bairro?.nome, slide?.cidade?.nome].map((v) => String(v ?? '').trim()).filter(Boolean);
    return partes.length ? partes.join(', ') : String(slide?.tipo?.nome || 'Imóvel');
};

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/** "350" (não "350,00"), "85,5", "1.250" — milhar com ponto SEM Intl (toLocaleString depende do ICU → hydration mismatch). */
export const areaFormatada = (valor: number): string => {
    if (!Number.isFinite(valor) || valor <= 0) return '';
    const inteiro = Math.floor(valor);
    const decimal = Math.round((valor - inteiro) * 100);
    const milhar = String(inteiro).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return decimal > 0 ? `${milhar},${String(decimal).padStart(2, '0').replace(/0$/, '')}` : milhar;
};

/** Até 4 atributos com valor > 0, na ordem da referência (quartos, suítes, vagas, área) — o CSS põe em caixa alta. */
export const atributosDoImovel = (slide: any, maximo = 4): string[] => {
    const n = (v: any) => Number(v) || 0;
    const itens: string[] = [];
    if (n(slide?.dormitorios) > 0) itens.push(plural(n(slide.dormitorios), 'quarto', 'quartos'));
    if (n(slide?.suites) > 0) itens.push(plural(n(slide.suites), 'suíte', 'suítes'));
    if (n(slide?.garagems) > 0) itens.push(plural(n(slide.garagems), 'vaga', 'vagas'));
    const area = slide?.area_privativa > 0 ? slide.area_privativa : slide?.area_total > 0 ? slide.area_total : slide?.terreno > 0 ? slide.terreno : 0;
    if (area > 0) itens.push(`${areaFormatada(Number(area))} ${slide?.area_privativa > 0 || slide?.area_total > 0 ? 'm²' : slide?.area_tipo || 'm²'}`);
    if (itens.length < maximo && n(slide?.banheiros) > 0) itens.push(plural(n(slide.banheiros), 'banheiro', 'banheiros'));
    return itens.slice(0, maximo);
};

/** Itens de menu sem repetição: mesma rota (sem query/hash e barra final) ou mesmo rótulo (sem acento/caixa) entram uma vez
 *  (o tenant que cadastra "Blog" no menu recebia o "Blog" automático em dobro). */
export const semDuplicatas = (itens: ItemMenu[]): ItemMenu[] => {
    const vistos = new Set<string>();
    return itens.filter((item) => {
        const rota = String(item.href || '').split('?')[0].split('#')[0].replace(/\/+$/, '').toLowerCase();
        const rotulo = removerAcentos(String(item.label || '')).toLowerCase().replace(/\s+/g, ' ').trim();
        const chaves = [rota && `r:${rota}`, rotulo && `l:${rotulo}`].filter(Boolean) as string[];
        if (chaves.some((c) => vistos.has(c))) return false;
        chaves.forEach((c) => vistos.add(c));
        return true;
    });
};

/** Inicial da empresa para a letra-marca gigante da faixa de contato (só letra/dígito; sem nome → ''). */
export const inicialDaEmpresa = (nome: string): string => {
    const m = /[A-Za-zÀ-ÿ0-9]/.exec(String(nome || ''));
    return m ? m[0].toUpperCase() : '';
};
